import { parseEntityRef } from '@backstage/catalog-model';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import { TextField, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import type { FieldValidation } from '@rjsf/utils';
import { useMemo } from 'react';
import { useOwnedEntities } from './useOwnedEntities';

export const OwnedEntityPickerFieldSchema = makeFieldSchema({
  output: z => z.string().describe('The entity reference for an owned catalog entity'),
  uiOptions: z =>
    z.object({
      techDocsOnly: z
        .boolean()
        .optional()
        .describe('Only show owned entities that already have TechDocs configured'),
    }),
});

type OwnedEntityPickerProps = typeof OwnedEntityPickerFieldSchema.TProps;

const OwnedEntityPicker = (props: OwnedEntityPickerProps) => {
  const {
    schema: { title = 'Catalog entity', description = 'Select an entity you own' },
    onChange,
    rawErrors,
    required,
    formData,
    errors,
    uiSchema,
  } = props;
  const techDocsOnly = uiSchema?.['ui:options']?.techDocsOnly ?? false;
  const { data: options = [], isLoading: loading } = useOwnedEntities(techDocsOnly);

  const selectedOption = useMemo(
    () => options.find(option => option.entityRef === formData) ?? null,
    [formData, options],
  );

  return (
    <ScaffolderField
      rawErrors={rawErrors}
      rawDescription={description}
      required={required}
      errors={errors}
    >
      <Autocomplete
        options={options}
        value={selectedOption}
        onChange={(_, newValue) => onChange(newValue?.entityRef)}
        getOptionLabel={option => option?.label ?? ''}
        getOptionSelected={(option, value) => option.entityRef === value.entityRef}
        loading={loading}
        noOptionsText={loading ? 'Loading your entities...' : 'No owned entities found'}
        renderOption={option => (
          <div>
            <Typography variant="body1">{option.label}</Typography>
            <Typography color="textSecondary" variant="caption" style={{ fontStyle: 'italic' }}>
              {option.caption}
            </Typography>
          </div>
        )}
        renderInput={params => (
          <TextField
            {...params}
            required={required}
            label={title}
            helperText={loading ? 'Loading entities you own' : undefined}
          />
        )}
      />
    </ScaffolderField>
  );
};

export default OwnedEntityPicker;

export const validateOwnedEntity = (entityRef: string | undefined, validation: FieldValidation) => {
  if (!entityRef) {
    return;
  }

  try {
    parseEntityRef(entityRef);
  } catch {
    validation.addError('Please select a valid catalog entity');
  }
};
